import { cn } from "../../utils/css";
import {
  Button,
  Calendar,
  PopoverContent,
  PopoverTrigger,
  Popover,
} from "@pushpress/pushpress-ui";
import { CalendarIcon } from "lucide-react";
import {
  format,
  isLeapYear,
  subDays,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  parseISO,
} from "date-fns";
import { DateRange } from "react-day-picker";
import {
  usePinnedDateSettingsQuery,
  usePinDateSettingsMutation,
} from "../../apis/userApi.hooks";
import { DatePresetType } from "../../apis/userApi";
import { useEffect, useState } from "react";

const QUICK_LINKS: Array<{
  label: string;
  value: DatePresetType;
  getRange: () => { start: Date; end: Date };
}> = [
  {
    label: "Today",
    value: "today",
    getRange: () => ({ start: new Date(), end: new Date() }),
  },
  {
    label: "Yesterday",
    value: "yesterday",
    getRange: () => ({
      start: subDays(new Date(), 1),
      end: subDays(new Date(), 1),
    }),
  },
  {
    label: "Week to Date",
    value: "week_to_date",
    getRange: () => ({ start: startOfWeek(new Date()), end: new Date() }),
  },
  {
    label: "Month to Date",
    value: "month_to_date",
    getRange: () => ({ start: startOfMonth(new Date()), end: new Date() }),
  },
  {
    label: "Quarter to Date",
    value: "quarter_to_date",
    getRange: () => ({ start: startOfQuarter(new Date()), end: new Date() }),
  },
  {
    label: "Year to Date",
    value: "year_to_date",
    getRange: () => ({ start: startOfYear(new Date()), end: new Date() }),
  },
  {
    label: "Last 7 Days",
    value: "last_7_days",
    getRange: () => ({ start: subDays(new Date(), 7), end: new Date() }),
  },
  {
    label: "Last 30 Days",
    value: "last_30_days",
    getRange: () => ({ start: subDays(new Date(), 30), end: new Date() }),
  },
];

export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  persistSelection = true,
  ...props
}: {
  startDate: Date;
  endDate: Date;
  onChange: (range: { start: Date; end: Date }, presetLabel?: string) => void;
  persistSelection?: boolean;
  [key: string]: any;
}) {
  const [activePreset, setActivePreset] = useState<
    DatePresetType | undefined
  >();
  const { data: pinnedSettings } = usePinnedDateSettingsQuery({
    enabled: persistSelection,
  });

  const { mutate: pinDateSettings } = usePinDateSettingsMutation();

  // Apply pinned settings when they load
  useEffect(() => {
    if (persistSelection && pinnedSettings) {
      if (pinnedSettings.preset) {
        const quickLink = QUICK_LINKS.find(
          (link) => link.value === pinnedSettings.preset,
        );
        if (quickLink) {
          setActivePreset(pinnedSettings.preset);
          const range = quickLink.getRange();
          onChange(range);
        }
      } else if (pinnedSettings.startDate && pinnedSettings.endDate) {
        setActivePreset(undefined);
        onChange({
          start: parseISO(pinnedSettings.startDate),
          end: parseISO(pinnedSettings.endDate),
        });
      }
    }
  }, [pinnedSettings, persistSelection]);

  const handleDateChange = (range: { start: Date; end: Date }) => {
    setActivePreset(undefined);
    onChange(range);

    if (persistSelection) {
      pinDateSettings({
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
      });
    }
  };

  const handlePresetClick = (link: (typeof QUICK_LINKS)[0]) => {
    setActivePreset(link.value);
    const range = link.getRange();
    onChange(range, link.label);

    if (persistSelection) {
      pinDateSettings({
        preset: link.value,
      });
    }
  };

  const currentYear = new Date().getFullYear();
  const maxDays = isLeapYear(currentYear) ? 366 : 365;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "tw-w-fit tw-justify-start tw-text-left tw-font-normal tw-text-xs tw-text-gray-500",
            !startDate && !endDate && "tw-text-muted-foreground",
          )}
        >
          <CalendarIcon className="tw-mr-2 tw-h-4 tw-w-4 tw-text-gray-500" />
          {activePreset ? (
            QUICK_LINKS.find((link) => link.value === activePreset)?.label ||
            `${format(startDate, "MM/dd/yy")} - ${format(endDate, "MM/dd/yy")}`
          ) : startDate && endDate ? (
            `${format(startDate, "MM/dd/yy")} - ${format(endDate, "MM/dd/yy")}`
          ) : (
            <span>Select a range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={4}
        className="tw-w-auto tw-p-0 tw-flex tw-flex-col md:tw-flex-row"
      >
        <div className="tw-min-w-[300px]">
          <Calendar
            mode="range"
            selected={{
              from: startDate,
              to: endDate,
            }}
            initialFocus
            onSelect={(range: DateRange | undefined) => {
              if (range?.from && range?.to) {
                handleDateChange({
                  start: range.from,
                  end: range.to,
                });
              }
            }}
            min={0}
            max={maxDays}
            {...props}
          />
        </div>

        {/* Quick Links Section */}
        <div className="tw-border-t md:tw-border-t-0 md:tw-border-l tw-border-gray-100 tw-w-full md:tw-w-[150px] tw-p-3">
          <div className="tw-flex tw-flex-row md:tw-flex-col tw-flex-wrap tw-gap-1">
            <p className="tw-text-xs tw-text-gray-600 tw-text-left tw-font-bold tw-w-full tw-pt-4 tw-pl-3">
              Date Ranges
            </p>
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-1 tw-gap-1 tw-w-full">
              {QUICK_LINKS.map((link) => (
                <Button
                  key={link.label}
                  variant={activePreset === link.value ? "dark" : "ghost"}
                  className={cn(
                    "!tw-h-8 !tw-justify-start",
                    "tw-text-xs tw-text-left",
                    activePreset === link.value
                      ? "tw-text-white"
                      : "tw-text-gray-600 hover:tw-text-gray-900 hover:tw-bg-gray-50",
                  )}
                  onClick={() => handlePresetClick(link)}
                >
                  {link.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
